import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  AppBar,
  Toolbar,
  List,
  Divider,
  Alert,
  Snackbar,
  Avatar
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import { paymentAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import BottomNav from '../components/BottomNavigation';

const PaymentMethods = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch payment methods from backend
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getPaymentMethods();
      if (response && response.success && response.data) {
        // Handle different possible response structures
        const methods = response.data.paymentMethods || response.data || [];
        setPaymentMethods(Array.isArray(methods) ? methods : []);
      } else {
        setError('Failed to load payment methods');
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setError('Failed to load payment methods');
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPaymentMethods();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleAddPaymentMethod = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleRemovePaymentMethod = (paymentMethodId) => {
    setPaymentMethods(prev => prev.filter(method => method.id !== paymentMethodId));
    setSnackbar({
      open: true,
      message: 'Payment method removed successfully',
      severity: 'success'
    });
  };

  const handleSetDefault = (paymentMethodId) => {
    setPaymentMethods(prev => prev.map(method => ({
      ...method,
      isDefault: method.id === paymentMethodId
    })));
    setSnackbar({
      open: true,
      message: 'Default payment method updated',
      severity: 'success'
    });
  };

  const getCardBrandIcon = (brand) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳'; // Visa icon
      case 'mastercard':
        return '💳'; // Mastercard icon
      case 'amex':
        return '💳'; // Amex icon
      default:
        return <CreditCardIcon />;
    }
  };

  const getCardBrandColor = (brand) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '#1a1f71'; // Visa blue
      case 'mastercard':
        return '#eb001b'; // Mastercard red
      case 'amex':
        return '#006fcf'; // Amex blue
      default:
        return '#666';
    }
  };

  const getCardBrandName = (brand) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return 'Visa';
      case 'mastercard':
        return 'Mastercard';
      case 'amex':
        return 'American Express';
      default:
        return brand;
    }
  };

  const formatExpiryDate = (month, year) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#0A2A66' }}>
        <Toolbar>
          <IconButton 
            color="inherit" 
            onClick={() => navigate('/profile')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Payment Methods
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md" sx={{ flex: 1, py: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Add Payment Method Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPaymentMethod}
            disabled={loading}
            sx={{
              backgroundColor: '#0A2A66',
              '&:hover': { backgroundColor: '#1a237e' }
            }}
          >
            Add Payment Method
          </Button>
        </Box>

        {/* Payment Methods List */}
        {loading ? (
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Loading payment methods...
            </Typography>
          </Card>
        ) : !paymentMethods || paymentMethods.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No payment methods saved
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a payment method to make checkout faster
            </Typography>
          </Card>
        ) : (
          <List sx={{ p: 0 }}>
            {paymentMethods && paymentMethods.map((method, index) => (
              <React.Fragment key={method?.id || index}>
                <Card sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: getCardBrandColor(method?.card?.brand || 'default'),
                            width: 48,
                            height: 48,
                            mr: 2
                          }}
                        >
                          {getCardBrandIcon(method?.card?.brand || 'default')}
                        </Avatar>
                        
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {getCardBrandName(method?.card?.brand || 'Card')} ending in {method?.card?.last4 || '****'}
                            </Typography>
                            {method?.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                icon={<CheckCircleIcon />}
                                sx={{ ml: 1, backgroundColor: '#4caf50', color: 'white' }}
                              />
                            )}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary">
                            Expires {formatExpiryDate(method?.card?.exp_month || 12, method?.card?.exp_year || 2025)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePaymentMethod(method?.id)}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {!method?.isDefault && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSetDefault(method?.id)}
                            sx={{ 
                              borderColor: '#0A2A66', 
                              color: '#0A2A66',
                              fontSize: '0.75rem'
                            }}
                          >
                            Set Default
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                {index < (paymentMethods?.length || 0) - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Security Notice */}
        <Card sx={{ mt: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              🔒 Your payment information is securely stored by Stripe and encrypted
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* Add Payment Method Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Payment Method
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Secure Payment Setup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your payment information will be securely processed by Stripe. 
              We never store your card details on our servers.
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              This feature will be integrated with Stripe Elements for secure payment processing.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button 
            onClick={handleCloseAddDialog}
            variant="contained"
            sx={{ backgroundColor: '#0A2A66' }}
          >
            Coming Soon
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentMethods;
